import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  Loader2,
  Receipt,
  Sparkles,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/credits-history")({
  head: () => ({
    meta: [
      { title: "クレジット使用履歴 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CreditsHistoryPage,
});

type TxRow = {
  id: string;
  created_at: string;
  amount: number;
  type: string;
  reason: string | null;
  generation_id: string | null;
};

type FilterTab = "all" | "consume" | "grant" | "refund";

const PAGE_SIZE = 20;
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2023 }, (_, i) => CURRENT_YEAR - i);

function describeTx(row: TxRow): string {
  const r = row.reason ?? "";
  if (row.type === "purchase") {
    if (r.startsWith("pack_")) return `クレジット購入（${r.replace("pack_", "")}）`;
    return "クレジット購入";
  }
  if (row.type === "grant") {
    if (r === "signup_bonus") return "新規登録ボーナス";
    if (r.startsWith("admin_grant:")) return `運営からの付与（${r.slice("admin_grant:".length)}）`;
    if (r === "referral_reward") return "友達紹介リワード";
    if (r.startsWith("subscription")) return "サブスク付与";
    return "クレジット付与";
  }
  if (row.type === "refund") {
    if (r === "submit_failed") return "返却（生成失敗）";
    return "返却";
  }
  if (row.type === "reserved" || row.type === "consume") {
    if (r.startsWith("generation:")) {
      const kind = r.slice("generation:".length);
      const label = ({
        trial: "お試し生成",
        i2v_long: "動画生成（長尺）",
        i2v_high: "動画生成（高品質）",
        i2v_standard: "動画生成（標準）",
        image: "画像生成",
        template: "テンプレート生成",
      } as Record<string, string>)[kind] ?? `生成（${kind}）`;
      return label;
    }
    return "消費";
  }
  if (row.type === "expire") return "有効期限切れ";
  return row.type;
}

function typeBadge(row: TxRow) {
  if (row.amount > 0) {
    if (row.type === "purchase") {
      return <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30 hover:bg-blue-500/15">購入</Badge>;
    }
    if (row.type === "grant") {
      return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/15">付与</Badge>;
    }
    if (row.type === "refund") {
      return <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30 hover:bg-amber-500/15">返却</Badge>;
    }
    return <Badge variant="secondary">+</Badge>;
  }
  return <Badge variant="outline" className="text-muted-foreground">消費</Badge>;
}

function CreditsHistoryPage() {
  const [year, setYear] = useState<string>("all");
  const [month, setMonth] = useState<string>("all");
  const [tab, setTab] = useState<FilterTab>("all");
  const [page, setPage] = useState(0);

  const queryKey = useMemo(
    () => ["credit_transactions", { year, month, tab }] as const,
    [year, month, tab],
  );

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: async (): Promise<TxRow[]> => {
      let q = supabase
        .from("credit_transactions")
        .select("id, created_at, amount, type, reason, generation_id")
        .order("created_at", { ascending: false })
        .limit(1000);

      if (year !== "all") {
        const y = parseInt(year, 10);
        const from = month === "all" ? new Date(y, 0, 1) : new Date(y, parseInt(month, 10) - 1, 1);
        const to = month === "all" ? new Date(y + 1, 0, 1) : new Date(y, parseInt(month, 10), 1);
        q = q.gte("created_at", from.toISOString()).lt("created_at", to.toISOString());
      }

      if (tab === "consume") {
        q = q.in("type", ["reserved", "consume", "expire"]);
      } else if (tab === "grant") {
        q = q.in("type", ["grant", "purchase"]);
      } else if (tab === "refund") {
        q = q.eq("type", "refund");
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as TxRow[];
    },
    staleTime: 30_000,
  });

  const rows = data ?? [];
  const totals = useMemo(() => {
    let inSum = 0;
    let outSum = 0;
    for (const r of rows) {
      if (r.amount > 0) inSum += r.amount;
      else outSum += r.amount;
    }
    return { inSum, outSum, net: inSum + outSum };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  function resetPage<T>(setter: (v: T) => void, value: T) {
    setter(value);
    setPage(0);
  }

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Coins className="h-6 w-6" />
            クレジット使用履歴
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            クレジットの増減をすべて確認できます
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/billing/history">
              <Receipt className="h-4 w-4 mr-2" />
              支払い履歴を見る
            </Link>
          </Button>
          <Button asChild>
            <Link to="/credits-shop">
              <Sparkles className="h-4 w-4 mr-2" />
              クレジットを購入
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">獲得合計</p>
            <p className="text-xl font-bold tabular-nums text-emerald-600">+{totals.inSum.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">消費合計</p>
            <p className="text-xl font-bold tabular-nums text-foreground">{totals.outSum.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">差引</p>
            <p className="text-xl font-bold tabular-nums">{(totals.net >= 0 ? "+" : "") + totals.net.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">フィルター</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={tab} onValueChange={(v) => resetPage(setTab, v as FilterTab)}>
            <TabsList className="grid grid-cols-4 w-full md:w-auto">
              <TabsTrigger value="all">すべて</TabsTrigger>
              <TabsTrigger value="consume">消費</TabsTrigger>
              <TabsTrigger value="grant">獲得</TabsTrigger>
              <TabsTrigger value="refund">返却</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">年度</label>
              <Select value={year} onValueChange={(v) => resetPage(setYear, v)}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}年</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">月</label>
              <Select value={month} onValueChange={(v) => resetPage(setMonth, v)} disabled={year === "all"}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <SelectItem key={m} value={String(m)}>{m}月</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <p className="text-muted-foreground">読み込みに失敗しました</p>
            <Button onClick={() => refetch()} variant="outline">再試行</Button>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-10 md:p-16 text-center space-y-4">
            <Coins className="h-14 w-14 mx-auto text-muted-foreground/50" />
            <h2 className="text-xl md:text-2xl font-semibold">まだクレジット履歴がありません</h2>
            <p className="text-sm text-muted-foreground">
              クレジットの獲得・消費があるとここに表示されます
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">日時</TableHead>
                    <TableHead>内容</TableHead>
                    <TableHead>区分</TableHead>
                    <TableHead className="text-right">クレジット</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {format(new Date(r.created_at), "yyyy/MM/dd HH:mm")}
                      </TableCell>
                      <TableCell className="text-sm">{describeTx(r)}</TableCell>
                      <TableCell>{typeBadge(r)}</TableCell>
                      <TableCell className={"text-right tabular-nums font-semibold " + (r.amount > 0 ? "text-emerald-600" : "text-foreground")}>
                        {r.amount > 0 ? "+" : ""}{r.amount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              {rows.length}件中 {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rows.length)}件を表示
              {isFetching && <Loader2 className="inline h-3 w-3 ml-2 animate-spin" />}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="tabular-nums">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
